import { useTranslation } from "react-i18next";
import { getCertificateIdLength } from "./utils/config";
import theme from "../../theme";
import { useFileValidation } from "./hooks/useFileValidation";
import FileDropzone from "../../Common/FileDropzone";
import FileDropzoneContent from "../../Common/FileDropzoneContent";
import DividerWithText from "../../Common/DividerWithText";
import useGetTranslatedCredentialType from "./utils/useGetTranslatedCredentialType";

import type { TFileValidationError } from "./hooks/useFileValidation";

// redux
import { useAppSelector } from "../../../redux/hooks";

// mui
import { makeStyles } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import clsx from "clsx";

type Props = {
    onSubmit: any;
    validatorContentRef: (node: HTMLDivElement) => HTMLDivElement | undefined;
    inputCredentialId: string;
    onChangeCredentialIdInput: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> | undefined;
    isHeaderShown: boolean;
    placeholderPrefix: string;
    environment: TEnv;
    api_identifier: string;
    brandName: TAccountType;
};

const useStyles = makeStyles({
    root: {
        "& label.Mui-focused": {
            borderColor: theme.palette.vbBlue.main,
            color: theme.palette.vbBlue.main,
        },
        "& label.MuiInputLabel-shrink": {
            color: theme.palette.vbBlue.main,
        },
        "& .MuiInput-underline:after": {
            borderColor: theme.palette.vbBlue.main,
        },
        "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
                borderColor: theme.palette.vbBlue.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.vbBlue.main,
            },
        },
    },
});

export default function CertificateNotFound(props: Props) {
    const { t } = useTranslation();
    const classes = useStyles();
    const getTranslatedCredentialType = useGetTranslatedCredentialType();

    const {
        handleFileDrop,
        loading,
        error: validationError,
    } = useFileValidation(props.environment, props.api_identifier, props.brandName);

    // redux
    const { certificateStatusCode } = useAppSelector((state) => ({
        certificateStatusCode: state.certificate_validator.certificateStatusCode,
    }));

    const hasError = certificateStatusCode !== 200;

    const getHelperText = () => {
        switch (certificateStatusCode) {
            case 410:
                return t("certificate_validator.not_found.error.410");
            case 404:
                return t("certificate_validator.not_found.error.404");
            default:
                return undefined;
        }
    };

    const renderValidationContent = (validationError: TFileValidationError) => {
        const getEventType = (fileType: string) => {
            switch (fileType) {
                case "png":
                    return getTranslatedCredentialType("badge", { firstLetterUpperCase: true });
                case "pdf":
                default:
                    return getTranslatedCredentialType("certificate", { firstLetterUpperCase: true });
            }
        };

        switch (validationError.error) {
            case "file-too-large":
                return (
                    <FileDropzoneContent
                        title={t(
                            "certificate_validator.upload_file.errors.file_too_large.title",
                            "Uploaded {{fileType}} is too large",
                            { fileType: validationError.fileType.toUpperCase() }
                        )}
                        description={t(
                            "certificate_validator.upload_file.errors.file_too_large.description",
                            "The file size should not exceed 20MB. Please try to upload another file to validate."
                        )}
                    />
                );
            case "file-invalid-type":
                return (
                    <FileDropzoneContent
                        title={t(
                            "certificate_validator.upload_file.errors.file_invalid_type.title",
                            "Unsupported file format"
                        )}
                        description={t(
                            "certificate_validator.upload_file.errors.file_invalid_type.description",
                            "The uploaded file format is not supported. Please upload a PDF or PNG file to validate again."
                        )}
                    />
                );
            case "certificate-id-missing":
                return (
                    <FileDropzoneContent
                        title={t(
                            "certificate_validator.upload_file.errors.certificate-id-missing.title",
                            "{{eventType}} ID required",
                            { eventType: getEventType(validationError.fileType) }
                        )}
                        description={t(
                            "certificate_validator.upload_file.errors.certificate-id-missing.description",
                            "Please enter the identification number of your {{eventType}} in the input field above.",
                            { eventType: getEventType(validationError.fileType) }
                        )}
                    />
                );
            default:
                return (
                    <FileDropzoneContent
                        title={t("certificate_validator.upload_file.errors.unknown.title", "Something went wrong")}
                        description={t(
                            "certificate_validator.upload_file.errors.unknown.description",
                            "It seems that something went wrong. Please try to upload a PDF or PNG file to validate again."
                        )}
                    />
                );
        }
    };

    return (
        <main
            id="main-content"
            aria-label={t("certificate_validator.accessibility.main_content", "Certificate validator")}
        >
            <div
                key="not-found"
                ref={props.validatorContentRef}
                className="d-flex flex-column align-items-center justify-content-center h-100 mt-3"
            >
                {/* not found */}
                <h1 className={clsx({ "sr-only": !props.isHeaderShown })}>
                    {t("certificate_validator.not_found.heading")}
                </h1>
                <p className="mt-3">{t("certificate_validator.not_found.subheading")}</p>
                <form
                    onSubmit={props.onSubmit}
                    role="search"
                    aria-label={t("certificate_validator.not_found.form_label", "Certificate search form")}
                    noValidate
                >
                    <div className="cv-not-found-form mt-4">
                        <TextField
                            variant="outlined"
                            id="credential-id"
                            name="credential-id"
                            label={t("certificate_validator.not_found.input_label")}
                            size="small"
                            style={{ width: 330 }}
                            inputProps={{
                                maxLength: getCertificateIdLength(props.placeholderPrefix),
                                "aria-invalid": hasError,
                                "aria-required": true,
                                autoCorrect: "off",
                                autoCapitalize: "off",
                                spellCheck: false,
                            }}
                            value={props.inputCredentialId}
                            onChange={props.onChangeCredentialIdInput}
                            placeholder={
                                props.placeholderPrefix
                                    ? `${props.placeholderPrefix}-xxxxxxx`
                                    : "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxx"
                            }
                            classes={classes}
                            error={hasError}
                            helperText={getHelperText()}
                            autoFocus
                        />
                        <Button
                            variant="outlined"
                            type="submit"
                            style={{
                                borderColor: theme.palette.vbBlue.main,
                                color: theme.palette.vbBlue.main,
                                padding: "9px 21px",
                            }}
                            disableRipple
                        >
                            {t("certificate_validator.not_found.button_label")}
                        </Button>
                    </div>
                </form>

                {/* Divider */}
                <DividerWithText
                    text={t("certificate_validator.not_found.or", "or")}
                    maxWidth="clamp(500px, 80%, 1000px)"
                    margin="30px 0"
                />

                {/* Drag and Drop Section */}
                <FileDropzone
                    onDrop={handleFileDrop}
                    loading={loading}
                    disabled={loading}
                    accept={{
                        "application/pdf": [".pdf"],
                        "image/png": [".png"],
                    }}
                    maxFiles={1}
                    validity={validationError ? "invalid" : "not_checked"}
                    maxSize={20 * 1024 * 1024} // 20MB
                    ariaLabel={t(
                        "certificate_validator.upload_file.dropzone_label",
                        "Click or drag a file here to upload the file (PDF or PNG)"
                    )}
                    inputId="file-upload-input"
                    maxWidth="550px"
                >
                    {validationError ? (
                        renderValidationContent(validationError)
                    ) : (
                        <FileDropzoneContent
                            title={t("certificate_validator.upload_file.title", "Do you have a digital file?")}
                            description={t(
                                "certificate_validator.upload_file.description",
                                "Upload your certificate (PDF) or badge (PNG) for validation. Click to select or drag & drop."
                            )}
                        />
                    )}
                </FileDropzone>
            </div>
        </main>
    );
}
